import { Box, Link, Spinner, VStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { Formik } from "formik"
import { useLocation } from "react-router-dom"
import {
  FeatureAction,
  FeatureKey,
  FeaturePermission,
  useContextualFeaturePermissionQuery,
  useCreateContextualFeaturePermissionMutation,
  useUpdateContextualFeaturePermissionMutation
} from "@pathwright/graphql-client/src/generated"
import FeatureBlockContainer from "../block/FeatureBlockContainer"
import FeaturePermissionsHomeScreenForm from "../form/FeaturePermissionsHomeScreenForm"

const getPermissionFromParam = (permissionParam: string): FeaturePermission => {
  switch (permissionParam) {
    case "no-one":
      return FeaturePermission.Disabled
    case "administrators":
      return FeaturePermission.AdminOnly
    case "all-staff":
      return FeaturePermission.StaffOnly
    case "everyone":
      return FeaturePermission.Everyone
    default:
      return FeaturePermission.Disabled
  }
}

export const ConfirmPreview = ({ card }: any) => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const permissionParam = searchParams.get("permission") || "no-one"

  const baseVariables = {
    context: null,
    featureKey: FeatureKey.HomeScreenPreview,
    featureAction: FeatureAction.View
  }

  const requestedPermissionLevel = getPermissionFromParam(permissionParam)

  const permissionFromParam =
    permissionParam === "no-one"
      ? FeaturePermission.Disabled
      : permissionParam === "administrators"
      ? FeaturePermission.AdminOnly
      : permissionParam === "all-staff"
      ? FeaturePermission.StaffOnly
      : FeaturePermission.Everyone

  const query = useContextualFeaturePermissionQuery({
    variables: baseVariables
  })
  const previewableCFP =
    query.data?.contextualFeaturePermission?.permission_in_context
  const previewableForTargetGroup = previewableCFP === permissionFromParam

  const [createMutation] = useCreateContextualFeaturePermissionMutation()
  const [updateMutation] = useUpdateContextualFeaturePermissionMutation()
  const mutation = query.data?.contextualFeaturePermission
    ? updateMutation
    : createMutation

  const getGroupName = (permission: FeaturePermission): string => {
    if (permission === FeaturePermission.AdminOnly) {
      return "Administrators"
    }
    if (permission === FeaturePermission.StaffOnly) {
      return "All Staff"
    }
    if (permission === FeaturePermission.Everyone) {
      return "Everyone"
    }
    return "No one" // Default fallback
  }

  const getFooterText = (permission: FeaturePermission): string => {
    const groupName = getGroupName(permission)

    if (permission === FeaturePermission.AdminOnly) {
      return `For ${groupName} only`
    }
    if (permission === FeaturePermission.StaffOnly) {
      return `For ${groupName} only`
    }
    if (permission === FeaturePermission.Everyone) {
      return `For ${groupName}`
    }
    return `For ${groupName}` // Default fallback
  }

  const footerText = getFooterText(permissionFromParam)

  if (query.loading) {
    return (
      <Card
        card={card}
        // title="Preview Home Screen Confirmation"
        noaction
        emphasis="secondary"
      >
        <CardBlock paddingTop={false}>
          <VStack py={4} justifyContent="center">
            <Spinner />
          </VStack>
        </CardBlock>
      </Card>
    )
  }

  if (query.error) {
    return (
      <Card
        card={card}
        // title="Preview Home Screen Confirmation"
        noaction
        emphasis="secondary"
      >
        <CardBlock paddingTop={false}>
          <Alert error={query.error} />
        </CardBlock>
      </Card>
    )
  }

  return (
    <Card
      card={card}
      // title="Preview Home Screen Confirmation"
      noaction
      emphasis="secondary"
    >
      <CardBlock paddingTop={false}>
        <Formik
          initialValues={{
            previewable: previewableForTargetGroup
          }}
          enableReinitialize
          onSubmit={async (values) => {
            await mutation({
              variables: {
                ...baseVariables,
                permissionInContext: permissionFromParam
              }
            })
            await query.refetch()
          }}
          onSubmitSuccess={() => {
            window.App?.navigate("/manage/school/features/lab/home/")
          }}
        >
          {(form) => {
            return (
              <Box as="form" minW="full" py={4}>
                <BlankSlate
                  icon="test-tube"
                  heading={
                    previewableForTargetGroup
                      ? `Preview Enabled`
                      : `Enable Preview`
                  }
                  body={
                    previewableForTargetGroup ? (
                      <>
                        The new Home Screen Preview is enabled for{" "}
                        {getGroupName(permissionFromParam)}.{" "}
                        <Link
                          href="https://help.pathwright.com/en/articles/510698-how-to-get-around"
                          target="_blank"
                          color="brand.400"
                          textDecoration="underline"
                        >
                          Learn more about the preview.
                        </Link>
                      </>
                    ) : (
                      <>
                        Enabling the new Home Screen Preview displays a preview
                        banner on the current Home Screen with a button to turn
                        it on. (They can switch back to the old Home Screen
                        anytime.){" "}
                        <Link
                          href="https://help.pathwright.com/en/articles/510698-how-to-get-around"
                          target="_blank"
                          color="brand.400"
                          textDecoration="underline"
                        >
                          Learn more about the preview.
                        </Link>
                      </>
                    )
                  }
                  primaryAction={
                    previewableForTargetGroup
                      ? null
                      : {
                          children: "Enable Preview",
                          onSubmit: form.submitForm
                        }
                  }
                  meta={previewableForTargetGroup ? null : footerText}
                />
              </Box>
            )
          }}
        </Formik>
      </CardBlock>
    </Card>
  )
}

export const ConfirmLaunch = ({ card }: any) => {
  const baseVariables = {
    context: null,
    featureKey: FeatureKey.HomeScreenUnified,
    featureAction: FeatureAction.HomeScreenView
  }

  const query = useContextualFeaturePermissionQuery({
    variables: baseVariables
  })

  const launchedCFP =
    query.data?.contextualFeaturePermission?.permission_in_context
  const isLaunched = launchedCFP === FeaturePermission.Everyone

  const [createMutation] = useCreateContextualFeaturePermissionMutation()
  const [updateMutation] = useUpdateContextualFeaturePermissionMutation()
  const mutation = query.data?.contextualFeaturePermission
    ? updateMutation
    : createMutation

  const handleSuccess = () => {
    window.App?.navigate("/manage/school/features/lab/home/")
  }

  if (query.loading) {
    return (
      <Card
        card={card}
        // title="Launch Home Screen Confirmation"
        noaction
        emphasis="secondary"
        style={{
          minHeight: "400px"
        }}
      >
        <CardBlock paddingTop={false}>
          <VStack py={4} justifyContent="center">
            <Spinner />
          </VStack>
        </CardBlock>
      </Card>
    )
  }

  if (query.error) {
    return (
      <Card
        card={card}
        // title="Launch Home Screen Confirmation"
        noaction
        emphasis="secondary"
        style={{
          minHeight: "400px"
        }}
      >
        <CardBlock paddingTop={false}>
          <Alert error={query.error} />
        </CardBlock>
      </Card>
    )
  }

  return (
    <Card
      card={card}
      // title="Launch Home Screen Confirmation"
      noaction
      emphasis="secondary"
    >
      <CardBlock paddingTop={false}>
        <Formik
          initialValues={{
            launched: isLaunched
          }}
          enableReinitialize
          onSubmit={async (values) => {
            if (!query.data) return
            if (isLaunched) return
            await mutation({
              variables: {
                ...baseVariables,
                permissionInContext: FeaturePermission.Everyone
              }
            })
            await query.refetch()
          }}
          onSubmitSuccess={() => {
            console.log("onSubmitSuccess")
            handleSuccess()
          }}
        >
          {(form) => {
            return (
              <Box as="form" minW="full" py={4}>
                <BlankSlate
                  icon="rocket"
                  heading={isLaunched ? "Launched!" : "Confirm Launch"}
                  body={
                    isLaunched ? (
                      <>
                        The New Home Screen is now launched for all members of
                        your Learning Space. Have questions or need to
                        temporarily revert the launch?{" "}
                        <Link
                          href="mailto:hello@pathwright.com"
                          target="_blank"
                          color="brand.400"
                          textDecoration="underline"
                        >
                          Contact us
                        </Link>
                      </>
                    ) : (
                      <>
                        Launching the new Home Screen replaces the current Home
                        Screen for all users. All their progress, tools, and
                        data will be preserved and shown in a new simpler and
                        more useful design.{" "}
                        <Link
                          href="https://help.pathwright.com/en/articles/510698-how-to-get-around"
                          target="_blank"
                          color="brand.400"
                          textDecoration="underline"
                        >
                          Learn more about the new Home Screen.
                        </Link>
                      </>
                    )
                  }
                  primaryAction={
                    isLaunched
                      ? null
                      : {
                          children: "Launch Home Screen",
                          onSubmit: form.submitForm
                        }
                  }
                  meta={
                    isLaunched ? "Launched for all members" : "For all members"
                  }
                />
              </Box>
            )
          }}
        </Formik>
      </CardBlock>
    </Card>
  )
}

const ManageHomeScreenFeaturePermissions = (props: { card: any }) => {
  const { card } = props

  return (
    <Card card={card} title="Lab Features" noaction emphasis="secondary">
      <CardBlock paddingTop={false}>
        <FeatureBlockContainer
          featureKey={FeatureKey.HomeScreenUnified}
          featureAction={FeatureAction.HomeScreenView}
          context={null}
          description={
            <>
              The New Home Screen unifies paths each member is learning,
              teaching, or designing onto one screen. Reorder, add, set aside
              Paths and more.{" "}
              {
                // TODO: home screen help article link
                false && (
                  <Link
                    href={
                      "https://help.pathwright.com/en/articles/510698-how-to-get-around"
                    }
                    target="_blank"
                    display="inline-flex"
                    alignItems="center"
                    color="brand.400"
                    textDecoration="underline"
                    gap={0}
                  >
                    Learn more
                    <Pathicon icon="chevron-right" size="0.8rem" />
                  </Link>
                )
              }
            </>
          }
          to={null}
        />
        <FeaturePermissionsHomeScreenForm />
      </CardBlock>
    </Card>
  )
}

export default ManageHomeScreenFeaturePermissions
